import React, { useCallback, useEffect } from 'react';
import useShortcut from '@uber/label-modalities/src/image/labelling/providers/hooks/use-shortcut';
import { styled } from 'baseui';
import { useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import ToolbarButton, { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import { BoundingBox, Click, Eraser, Fill } from '../../../icons';
import Brush from '../../../icons/brush';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { isSAM3EnabledAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { GRID_MODE, SAM_CLICK, SEGMENTATION_TOOLS } from '../../constants';
import { useIsToolDisabled } from '../../providers/hooks/use-is-tool-disabled';
import { useSam3ForAutoDetectMask } from '../../providers/hooks/use-sam3-for-auto-detect-mask';
import { handleShortcutRegistrationError } from '../../providers/utils/shortcut';
import useAnalyticsForToolBar from './analytics/useAnalyticsForToolBar';
import AutoDetectToolbarButton from './auto-detect-toolbar-button';
const SAM_CLICK_LABEL = 'Click on the region to begin segmentation';
const SAM_BOUNDING_BOX_LABEL = 'Draw a box around the desired area to segment object within';
const Icons = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: '10px'
});
const HorizontalDivider = styled('div', ({
  $theme: theme
}) => ({
  height: theme.sizing.scale900,
  ...theme.borders.border200,
  margin: theme.sizing.scale0,
  marginLeft: theme.sizing.scale200
}));
const SegmentationPanelIcons = ({
  localStrokeSize,
  selectedClassName
}: {
  localStrokeSize: number[];
  selectedClassName: string;
}) => {
  const {
    brushToolSettingsAtom,
    gridModeAtom,
    snapEnabledAtom,
    dispatchAtom
  } = useStageAtoms();
  const {
    selectedTool
  } = useAtomValue(brushToolSettingsAtom);
  const gridMode = useAtomValue(gridModeAtom);
  const snapEnabled = useAtomValue(snapEnabledAtom);
  const dispatchAction = useSetAtom(dispatchAtom);
  const isSAM3Enabled = useAtomValue(isSAM3EnabledAtom);
  const {
    registerHotKeys
  } = useShortcut();
  const {
    sendEventToolSelected
  } = useAnalyticsForToolBar();
  const [_, theme] = useStyletron();
  const {
    detect,
    submitAll,
    cancelAll,
    isDetecting,
    detectedCount,
    pendingAnnotationLabel
  } = useSam3ForAutoDetectMask();
  const onPolygonSelect = () => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.SEGMENTATION_POLYGON,
      value: localStrokeSize.toString(),
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.SEGMENTATION_POLYGON,
        eraserSelected: false,
        size: localStrokeSize[0]
      }
    });
  };
  const onSamClickModelSelection = useCallback(() => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.SAM_CLICK,
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.SAM_CLICK,
        eraserSelected: false,
        inclusionExclusion: SAM_CLICK.INCLUDE
      }
    });
  }, [sendEventToolSelected, dispatchAction, gridMode, snapEnabled]);
  const onSamBoundingBoxModelSelection = useCallback(() => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.SAM_BOUNDING_BOX,
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.SAM_BOUNDING_BOX,
        eraserSelected: false
      }
    });
  }, [sendEventToolSelected, dispatchAction, gridMode, snapEnabled]);
  const onBrushChange = () => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.BRUSH,
      value: localStrokeSize.toString(),
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.BRUSH,
        eraserSelected: false,
        size: localStrokeSize[0]
      }
    });
  };
  const onEraserChange = () => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.ERASER,
      value: localStrokeSize.toString(),
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.ERASER,
        eraserSelected: true,
        size: localStrokeSize[0]
      }
    });
  };
  const onAutoDetectSelect = () => {
    sendEventToolSelected({
      tool: SEGMENTATION_TOOLS.AUTO_DETECT,
      gridEnabled: gridMode !== GRID_MODE.NONE,
      snapEnabled
    });
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SEGMENTATION_TOOLS.AUTO_DETECT,
        eraserSelected: false
      }
    });
  };
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['z'],
      onAction: onSamBoundingBoxModelSelection
    }, {
      keys: ['m'],
      onAction: onSamClickModelSelection
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, onSamClickModelSelection, onSamBoundingBoxModelSelection]);
  const isToolDisabled = useIsToolDisabled();
  return <Icons>
      {<>
          <ToolbarButton title={SAM_CLICK_LABEL} onClick={onSamClickModelSelection} attributes={{
        'data-testid': 'sam_click'
      }} isSelected={selectedTool === SEGMENTATION_TOOLS.SAM_CLICK} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
            <Click fillColor={theme.colors.primary} />
          </ToolbarButton>
          <ToolbarButton title={SAM_BOUNDING_BOX_LABEL} onClick={onSamBoundingBoxModelSelection} attributes={{
        'data-testid': 'sam_bounding_box'
      }} isSelected={selectedTool === SEGMENTATION_TOOLS.SAM_BOUNDING_BOX} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled} customStyles={{
        padding: theme.sizing.scale200
      }}>
            <BoundingBox fillColor={theme.colors.primary} />
          </ToolbarButton>
          <HorizontalDivider />
        </>}
      <ToolbarButton title="Brush" onClick={onBrushChange} attributes={{
      'data-testid': 'brush'
    }} isSelected={selectedTool === SEGMENTATION_TOOLS.BRUSH} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
        <Brush fillColor={theme.colors.primary} />
      </ToolbarButton>
      <ToolbarButton title="Eraser" onClick={onEraserChange} attributes={{
      'data-testid': 'eraser'
    }} isSelected={selectedTool === SEGMENTATION_TOOLS.ERASER} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
        <Eraser fillColor={theme.colors.primary} />
      </ToolbarButton>
      <ToolbarButton title="Polygon" onClick={onPolygonSelect} attributes={{
      'data-testid': 'polygon'
    }} isSelected={selectedTool === SEGMENTATION_TOOLS.SEGMENTATION_POLYGON} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
        <Fill fillColor={theme.colors.primary} />
      </ToolbarButton>

      {isSAM3Enabled && <>
          <HorizontalDivider />
          <AutoDetectToolbarButton detect={detect} submitAll={submitAll} cancelAll={cancelAll} isDetecting={isDetecting} detectedCount={detectedCount} selectedClassName={selectedClassName} pendingAnnotationLabel={pendingAnnotationLabel} isSelected={selectedTool === SEGMENTATION_TOOLS.AUTO_DETECT} disabled={isToolDisabled} testId="auto_detect" onSelect={onAutoDetectSelect} />
        </>}
    </Icons>;
};
export default SegmentationPanelIcons;